import { useState, useCallback } from "react";
import { Smile, Reply, Copy, Trash2, Check } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type HoverToolbarProps = {
  canDelete: boolean;
  plainText: string;
  onReact?: () => void;
  onReply?: () => void;
  onDelete?: () => void;
};

export function HoverToolbar({
  canDelete,
  plainText,
  onReact,
  onReply,
  onDelete,
}: HoverToolbarProps) {
  const [showCopied, setShowCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = plainText;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1500);
    }
  }, [plainText]);

  return (
    <TooltipProvider delayDuration={0}>
      <div
        className={cn(
          "absolute -top-3 z-10 flex items-center gap-0.5",
          "nav-glass rounded-full shadow-sm px-1 py-0.5",
          "opacity-0 group-hover/bubble:opacity-100 transition-opacity duration-100",
          "pointer-events-none group-hover/bubble:pointer-events-auto"
        )}
        style={{ right: 8 }}
      >
        {onReact && (
          <ToolbarButton icon={Smile} label="React" onClick={onReact} />
        )}
        {onReply && (
          <ToolbarButton icon={Reply} label="Reply" onClick={onReply} />
        )}
        <Tooltip open={showCopied || undefined}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={handleCopy}
              className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Copy"
            >
              {showCopied ? (
                <Check className="h-3.5 w-3.5 text-green-600" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </button>
          </TooltipTrigger>
          {showCopied && (
            <TooltipContent side="top" className="text-xs">
              Copied
            </TooltipContent>
          )}
        </Tooltip>
        {canDelete && onDelete && (
          <ToolbarButton
            icon={Trash2}
            label="Delete"
            onClick={onDelete}
            className="text-destructive hover:text-destructive"
          />
        )}
      </div>
    </TooltipProvider>
  );
}

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  className,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          className={cn(
            "h-7 w-7 flex items-center justify-center rounded-full hover:bg-muted/50 transition-colors text-muted-foreground hover:text-foreground",
            className
          )}
          aria-label={label}
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-xs">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
